import { ref, computed, onUnmounted, type Ref } from 'vue';
import { QTool, isConnectedMode } from '@/native/plugins/QTool';
import type {
  QToolDevice,
  ReadoutDeviceResult,
  QToolMode,
  QToolReadoutState,
} from '@/native/plugins/QTool';
import type { FilterListItem } from '@/native/plugins/WebEvents';
import type { PluginListenerHandle } from '@capacitor/core';

export interface MatchedDevice {
  serialNumber: string;
  type: string;
  rssi: number | null;
  isFound: boolean;
}

const WEAK_SIGNAL_THRESHOLD = -50;

export function isStrongSignal(rssi: number | null): boolean {
  return rssi !== null && rssi > WEAK_SIGNAL_THRESHOLD;
}

export function useQTool(filterItems: Ref<FilterListItem[]>) {
  const discoveredQTools = ref<QToolDevice[]>([]);
  const mode = ref<QToolMode>({ type: 'idle' });
  const readoutState = ref<QToolReadoutState>('idle');
  const readoutDevices = ref<ReadoutDeviceResult[]>([]);

  const listeners: PluginListenerHandle[] = [];

  const filterDevices = computed<MatchedDevice[]>(() => {
    const compareSerial = (a: string, b: string) =>
      a === b || parseInt(a, 10) === parseInt(b, 10);

    return filterItems.value
      .map((filterItem) => {
        const found = readoutDevices.value.find((d) =>
          compareSerial(d.serialNumber, filterItem.serialNumber)
        );
        if (found) {
          return {
            serialNumber: filterItem.serialNumber,
            type: filterItem.type,
            rssi: found.rssi,
            isFound: true,
          };
        }
        return {
          serialNumber: filterItem.serialNumber,
          type: filterItem.type,
          rssi: null,
          isFound: false,
        };
      })
      .sort((a, b) => (b.rssi ?? -Infinity) - (a.rssi ?? -Infinity));
  });

  const matchedCount = computed(
    () => filterDevices.value.filter((d) => d.isFound).length
  );

  const isConnected = computed(() => isConnectedMode(mode.value));
  const connectedQToolName = computed(() =>
    'qtool' in mode.value ? mode.value.qtool.name : undefined
  );

  async function setupListeners() {
    listeners.push(
      await QTool.addListener('qtoolDiscoveryResult', (data) => {
        discoveredQTools.value = data.qtools;
      })
    );

    listeners.push(
      await QTool.addListener('qtoolModeChanged', (data) => {
        mode.value = data;
      })
    );

    listeners.push(
      await QTool.addListener('qtoolReadoutDevice', (device) => {
        const existing = readoutDevices.value.filter(
          (d) => d.serialNumber !== device.serialNumber
        );
        readoutDevices.value = [device, ...existing].sort(
          (a, b) => (b.rssi ?? -Infinity) - (a.rssi ?? -Infinity)
        );
      })
    );

    listeners.push(
      await QTool.addListener('qtoolReadoutStateChanged', (data) => {
        readoutState.value = data.state;
      })
    );
  }

  async function startDiscovery() {
    readoutDevices.value = [];
    mode.value = { type: 'idle' };
    await QTool.startQToolDiscovery();
  }

  async function connectToQTool(address: string) {
    readoutDevices.value = [];
    await QTool.connect({ address });
  }

  async function stop() {
    await QTool.stopReadout();
  }

  async function disconnect() {
    readoutDevices.value = [];
    await QTool.disconnect();
  }

  async function cleanup() {
    for (const listener of listeners) {
      await listener.remove();
    }
    listeners.length = 0;
  }

  setupListeners();
  onUnmounted(cleanup);

  return {
    discoveredQTools,
    mode,
    connectedQToolName,
    readoutState,
    readoutDevices,
    filterDevices,
    matchedCount,
    isConnected,
    startDiscovery,
    connectToQTool,
    stop,
    disconnect,
    cleanup,
  };
}
